import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

const Header = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const isHomePage = location.pathname === '/';

  const toggleMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const handleNavigation = (sectionId) => {
    if (isHomePage) {
      // If we're on home page, scroll to section
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    } else {
      // If we're on another page, navigate to home with hash
      navigate(`/#${sectionId}`);
    }
    
    // Close mobile menu if open
    setIsMobileMenuOpen(false);
  };

  const handleHomeClick = () => {
    navigate('/');
    setIsMobileMenuOpen(false);
  };

  const handleContactClick = () => {
    window.location.href = 'tel:+919873030848';
  };

  const handleEmailClick = () => {
    window.location.href = 'mailto:info@akiradvisory.in';
  };

  return (
    <header
      id="header"
      className="sticky top-0 z-50 bg-white shadow-lg border-b border-gray-100"
      style={{ fontFamily: "'Inter', sans-serif" }}
    >
      {/* Top Contact Bar */}
      <div className="bg-blue-600/90 text-white py-2">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center">
            {/* Call Us - Aligned with logo */}
            <div className="flex items-center gap-2 mb-2 md:mb-0">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-green-200">
                <path d="M22 16.92v3.08a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-5.6-5.6A19.79 19.79 0 0 1 2 4.18 2 2 0 0 1 4.1 2h3.08a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
              </svg>
              <a 
                href="tel:+919873030848" 
                className="hover:text-blue-100 transition-colors duration-200 font-medium text-sm"
                onClick={handleContactClick}
              >
                +91-9873030848
              </a>
            </div>
            
            {/* Email - Middle */}
            <div className="flex items-center gap-1 hover:text-blue-100 transition-colors duration-200 text-sm mb-2 md:mb-0">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-blue-200">
                <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                <polyline points="22,6 12,13 2,6"/>
              </svg>
              <a 
                href="mailto:info@akiradvisory.in" 
                className="ml-1"
                onClick={handleEmailClick}
              >
                info@akiradvisory.in
              </a>
            </div>
            
            {/* Address - Aligned with contact button */}
            <div className="flex items-center gap-1 text-sm">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-purple-200">
                <path d="M21 10c0 7-9 12-9 12s-9-5-9-12a9 9 0 0 1 18 0z"/>
                <circle cx="12" cy="10" r="3"/>
              </svg>
              <span className="hidden lg:inline">357, Surya Nagar, Phase-II, Faridabad, Haryana</span>
              <span className="lg:hidden">Faridabad, Haryana</span>
            </div>
          </div>
        </div>
      </div>

      {/* Original Main Navigation Bar */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <div 
            onClick={handleHomeClick}
            className="flex items-center space-x-3 p-2 rounded-xl transition-all duration-300 hover:bg-blue-50 group cursor-pointer"
          >
            <img src='/assets/images/logo.jpg' alt="logo" className="h-10 w-auto" />
          </div>

          {/* Desktop Navigation Menu */}
          <nav className="hidden md:flex space-x-1">
            <button
              onClick={() => handleNavigation('home')}
              className="text-gray-600 hover:text-blue-700 font-medium transition-all duration-300 py-2 px-4 rounded-xl hover:bg-blue-50"
            >
              Home
            </button>
            <button
              onClick={() => handleNavigation('services')}
              className="text-gray-600 hover:text-blue-700 font-medium transition-all duration-300 py-2 px-4 rounded-xl hover:bg-blue-50"
            >
              Services
            </button>
            <button
              onClick={() => handleNavigation('impact')}
              className="text-gray-600 hover:text-blue-700 font-medium transition-all duration-300 py-2 px-4 rounded-xl hover:bg-blue-50"
            >
              Impact
            </button>
            <button
              onClick={() => handleNavigation('about')}
              className="text-gray-600 hover:text-blue-700 font-medium transition-all duration-300 py-2 px-4 rounded-xl hover:bg-blue-50"
            >
              About Us
            </button>
            <button
              onClick={() => handleNavigation('contact')}
              className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-6 rounded-xl shadow-lg transition-all duration-300 transform hover:scale-105 ml-2"
            >
              Contact
            </button>
          </nav>

          {/* Mobile Menu Button */}
          <button
            onClick={toggleMenu}
            className="md:hidden p-2 rounded-xl text-gray-500 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 transition hover:bg-gray-100"
          >
            <svg
              className="h-6 w-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              {isMobileMenuOpen ? (
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M6 18L18 6M6 6l12 12"
                />
              ) : (
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M4 6h16M4 12h16M4 18h16"
                />
              )}
            </svg>
          </button>
        </div>
      </div>

      {/* Mobile Menu Dropdown */}
      <div
        className={`md:hidden bg-white border-t border-gray-100 shadow-xl transition-all duration-300 ${
          isMobileMenuOpen ? 'block' : 'hidden'
        }`}
      >
        <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3">
          {/* Mobile Contact Info */}
          <div className="px-4 py-3 border-b border-gray-100 bg-blue-50 rounded-lg mx-2 mb-2">
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-green-600">
                  <path d="M22 16.92v3.08a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-5.6-5.6A19.79 19.79 0 0 1 2 4.18 2 2 0 0 1 4.1 2h3.08a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
                </svg>
                <a 
                  href="tel:+919873030848" 
                  className="text-gray-700 hover:text-blue-600 transition-colors font-medium"
                  onClick={handleContactClick}
                >
                  <span className="text-sm ml-1">+91-9873030848</span>
                </a>
              </div>
              <div className="flex items-center gap-1 text-gray-700 hover:text-blue-600 transition-colors">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-blue-600">
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                  <polyline points="22,6 12,13 2,6"/>
                </svg>
                <a 
                  href="mailto:info@akiradvisory.in" 
                  className="text-sm ml-1"
                  onClick={handleEmailClick}
                >
                  info@akiradvisory.in
                </a>
              </div>
              <div className="flex items-start gap-1 text-gray-700">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-purple-600 mt-0.5">
                  <path d="M21 10c0 7-9 12-9 12s-9-5-9-12a9 9 0 0 1 18 0z"/>
                  <circle cx="12" cy="10" r="3"/>
                </svg>
                <span className="text-sm ml-1">357, Surya Nagar, Phase-II,<br />Faridabad, Haryana</span>
              </div>
            </div>
          </div>

          {/* Navigation Links */}
          <button
            onClick={() => handleNavigation('home')}
            className="block w-full text-left px-4 py-3 rounded-xl text-base font-medium text-gray-700 hover:bg-blue-50 hover:text-blue-600 transition"
          >
            Home
          </button>
          <button
            onClick={() => handleNavigation('services')}
            className="block w-full text-left px-4 py-3 rounded-xl text-base font-medium text-gray-700 hover:bg-blue-50 hover:text-blue-600 transition"
          >
            Services
          </button>
          <button
            onClick={() => handleNavigation('impact')}
            className="block w-full text-left px-4 py-3 rounded-xl text-base font-medium text-white bg-blue-600 hover:bg-blue-700 transition"
          >
            Impact
          </button>
          <button
            onClick={() => handleNavigation('about')}
            className="block w-full text-left px-4 py-3 rounded-xl text-base font-medium text-gray-700 hover:bg-blue-50 hover:text-blue-600 transition"
          >
            About Us
          </button>
          <button
            onClick={() => handleNavigation('contact')}
            className="block w-full text-left px-4 py-3 rounded-xl text-base font-medium text-gray-700 hover:bg-blue-50 hover:text-blue-600 transition"
          >
            Contact
          </button>
        </div>
      </div>
    </header>
  );
};

export default Header;